import type React from "react";
import { memo, useCallback, useEffect, useState } from "react";
import { ColorService, type IColor } from "react-color-palette";
import { cn } from "../../../util/classNameConstructor";
import Button from "../button/Button";
import { Input } from "./Input";

function float(value: number, decimalPlaces: number): number {
  return Math.round(value * 10 ** decimalPlaces) / 10 ** decimalPlaces;
}

function formatRgb({ r, g, b, a }: IColor["rgb"]): string {
  const rgb: (number | string)[] = [
    Math.round(r),
    Math.round(g),
    Math.round(b),
  ];
  const alpha = float(a, 3);

  if (alpha < 1) rgb.push(alpha);

  return rgb.join(", ");
}

function formatHex(color: string): string {
  const sanitizedColor = color.replace(/#/g, "");
  const hexColorPattern = /^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
  if (hexColorPattern.test(sanitizedColor)) {
    return sanitizedColor.toUpperCase();
  }
  return sanitizedColor.replace(/[^0-9a-fA-F]/g, "F").toUpperCase();
}

function percentageToHex(percentage: string): string {
  if (!percentage || percentage === "") return "";
  const numValue = Number.parseInt(percentage, 10);
  if (Number.isNaN(numValue) || numValue < 0 || numValue > 100) return "";
  const hexValue = Math.round((numValue * 255) / 100);
  return hexValue.toString(16).padStart(2, "0").toUpperCase();
}

function hexToPercentage(hexValue: string): string {
  if (!hexValue || hexValue.length !== 2) return "";
  const numValue = Number.parseInt(hexValue, 16);
  if (Number.isNaN(numValue)) return "";
  return Math.round((numValue / 255) * 100).toString();
}

function validateHex(colorValue: string, strict = true): string | false {
  const value = formatHex(colorValue || "");
  if (value.length >= 6) {
    return value.slice(0, 8).toUpperCase(); // Allow up to 8 chars for RGBA
  }
  if (value.length === 3 && !strict) {
    return value
      .split("")
      .map((char) => char.repeat(2))
      .join("")
      .toUpperCase();
  }
  return false;
}

function validateRgb(colorValue: string): string | false {
  const values = colorValue?.replaceAll(" ", "").split(",");
  if (!values || (values.length !== 3 && values.length !== 4)) {
    return false;
  }

  const [r, g, b, a] = values;
  const isValidComponent = (component: string, max = 255) =>
    component.match(/^[0-9]*\.?[0-9]+$/) &&
    Number(component) >= 0 &&
    Number(component) <= max;

  const isValid =
    isValidComponent(r) &&
    isValidComponent(g) &&
    isValidComponent(b) &&
    (a === undefined || isValidComponent(a, 1));

  return isValid ? values.join(",") : false;
}

interface ColorFieldsProps {
  readonly color: IColor;
  readonly onChange: (color: IColor) => void;
  readonly onSave: (color: IColor) => void;
  readonly hideInput?: boolean;
}

interface FieldState {
  hex: string;
  rgb: string;
}

export const ColorPickerFields = memo<ColorFieldsProps>(
  ({ color, onChange, onSave, hideInput = false }) => {
    const [fields, setFields] = useState<FieldState>({
      hex: formatHex(color.hex),
      rgb: formatRgb(color.rgb),
    });

    // Update fields when color prop changes
    useEffect(() => {
      const newHex = formatHex(color.hex);
      const newRgb = formatRgb(color.rgb);

      setFields((prev) => {
        // Only update if values have actually changed to prevent unnecessary re-renders
        if (prev.hex !== newHex || prev.rgb !== newRgb) {
          return { hex: newHex, rgb: newRgb };
        }
        return prev;
      });
    }, [color]);

    const applyColorChange = useCallback(
      (field: keyof FieldState, value: string) => {
        if (!value) return;

        try {
          if (field === "rgb") {
            const values = value.split(",").map(Number);
            const rgbColor = {
              r: values[0] || 0,
              g: values[1] || 0,
              b: values[2] || 0,
              a: values[3] !== undefined ? values[3] : 1,
            };
            onChange(ColorService.convert("rgb", rgbColor));
          } else {
            onChange(ColorService.convert("hex", `#${value.replace("#", "")}`));
          }
        } catch (error) {
          console.warn("Failed to apply color change:", error);
        }
      },
      [onChange],
    );

    const handleFieldChange = useCallback(
      (field: keyof FieldState, index?: number) =>
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          const { value } = event.target;

          let newValue: string;

          if (field === "hex") {
            if (index === 0) {
              // Main hex color input
              newValue = formatHex(value);
            } else {
              // Alpha percentage input
              const hexAlpha = percentageToHex(value);
              const currentHex = fields.hex.slice(0, 6);
              newValue = `${currentHex}${hexAlpha}`;
            }
          } else {
            // Handle RGB individual component changes
            const currentValues = fields.rgb.split(",").map((v) => v.trim());
            let formattedValue = value
              .trim()
              .slice(0, index === 3 ? 3 : 5)
              .replace(",", ".");

            if (index !== undefined) {
              if (index === 3) {
                // Convert alpha percentage to decimal
                const percentValue = Number.parseFloat(formattedValue);
                formattedValue = Number.isNaN(percentValue)
                  ? ""
                  : (percentValue / 100).toString();
              }

              if (index < currentValues.length) {
                currentValues[index] = formattedValue;
              } else {
                currentValues.push(formattedValue);
              }
              newValue = currentValues.join(",");
            } else {
              newValue = value;
            }
          }

          setFields((prev) => ({ ...prev, [field]: newValue }));

          // Validate and apply changes
          const validator = field === "hex" ? validateHex : validateRgb;
          const validatedValue = validator(newValue);

          if (validatedValue) {
            applyColorChange(field, validatedValue);
          }
        },
      [fields, applyColorChange],
    );

    const handleFieldBlur = useCallback(
      (field: keyof FieldState) => () => {
        const currentValue = fields[field];
        const validator = field === "hex" ? validateHex : validateRgb;
        const validatedValue = validator(currentValue, false);

        if (validatedValue) {
          // Update fields with validated value if it's different
          if (currentValue !== validatedValue) {
            setFields((prev) => ({ ...prev, [field]: validatedValue }));
          }
          applyColorChange(field, validatedValue);
        } else {
          // Reset to current color value if validation fails
          const resetValue =
            field === "hex" ? formatHex(color.hex) : formatRgb(color.rgb);
          setFields((prev) => ({ ...prev, [field]: resetValue }));
        }
      },
      [fields, color, applyColorChange],
    );

    const handleSave = useCallback(() => {
      onSave(color);
    }, [onSave, color]);

    const rgbValues = fields.rgb.split(",").map((v) => v.trim());
    const rgbComponents = ["R", "G", "B", "A"];

    // Calculate alpha percentage for hex display
    const hexAlphaValue = fields.hex.length > 6 ? fields.hex.slice(6, 8) : "";
    const alphaPercentage = hexAlphaValue
      ? hexToPercentage(hexAlphaValue)
      : "100";

    return (
      <div className="flex flex-col w-full items-end gap-3">
        {!hideInput && (
          <div className="grid grid-cols-[auto_1fr] gap-2 w-full">
            {/* HEX Input */}
            <div className="grid col-span-2 grid-cols-subgrid items-center w-full">
              <label htmlFor="hex-input" className="text-text-subdued text-sm">
                HEX
              </label>
              <div className="grid grid-cols-[3fr_1fr] w-full">
                <Input
                  id="hex-input"
                  labelOutside
                  classNames={{
                    container: "focus-within:z-10",
                    inputContainer: "rounded-e-none px-2 -me-[0.5px] w-auto",
                  }}
                  onChange={handleFieldChange("hex", 0)}
                  onBlur={handleFieldBlur("hex")}
                  value={fields.hex.slice(0, 6)}
                  size="md"
                  prepend={<span className="text-text-subdued text-sm">#</span>}
                />
                <Input
                  id="hex-input-percent"
                  labelOutside
                  classNames={{
                    container: "focus-within:z-10",
                    inputContainer: "rounded-s-none -ms-[0.5px] w-auto px-2",
                  }}
                  onChange={handleFieldChange("hex", 1)}
                  onBlur={handleFieldBlur("hex")}
                  value={alphaPercentage}
                  append={<span className="text-text-subdued text-sm">%</span>}
                  size="md"
                />
              </div>
            </div>

            {/* RGB Inputs */}
            <div className="grid col-span-2 grid-cols-subgrid items-center w-full">
              <label htmlFor="rgb-r" className="text-text-subdued text-sm">
                RGB
              </label>
              <div className="grid grid-cols-4">
                {rgbComponents.map((component, index, arr) => {
                  // Calculate display value for alpha component
                  const displayValue =
                    index === 3 &&
                    !Number.isNaN(Number.parseFloat(rgbValues[index]))
                      ? Math.round(
                          Number.parseFloat(rgbValues[index] || "") * 100,
                        ).toString()
                      : rgbValues[index] || "";

                  return (
                    <Input
                      key={component}
                      id={`rgb-${component.toLowerCase()}`}
                      labelOutside
                      classNames={{
                        container: "focus-within:z-10",
                        inputContainer: cn(
                          "px-2",
                          index === 0
                            ? "rounded-e-none -me-[0.5px]"
                            : index === arr.length - 1
                              ? "rounded-s-none -ms-[0.5px]"
                              : "rounded-none -mx-[0.5px] w-auto",
                        ),
                      }}
                      onChange={handleFieldChange("rgb", index)}
                      onBlur={handleFieldBlur("rgb")}
                      value={index === 3 ? displayValue || "100" : displayValue}
                      size="md"
                      append={
                        component === "A" ? (
                          <span className="text-text-subdued text-sm">%</span>
                        ) : undefined
                      }
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <div className="flex w-full">
          <Button
            variant="primary"
            id="color-picker-save"
            leadingIcon="checkmark-md"
            onClick={handleSave}
            classNameWrapper="shrink-0 ms-auto"
          />
        </div>
      </div>
    );
  },
);
